import { useEffect, useId, useRef } from "react";
import { useNavigate } from "react-router";
import { useT } from "./TeamUi";

// OperatingDraftGuard: con cambios sin guardar, salir por un enlace, por el botón atrás o cerrando la pestaña pregunta
// «¿Descartar los cambios?» (C12) · «Seguir editando» · «Descartar». El foco vuelve a quien lo pidió.

export function DraftGuard({ dirty }: { dirty: boolean }) {
  const T = useT();
  const navigate = useNavigate();
  const dialog = useRef<HTMLDialogElement>(null);
  const id = `cfg1-leave-${useId().replace(/:/g, "")}`;
  const state = useRef({ dirty, leaving: false, pending: null as null | (() => void) });
  useEffect(() => {
    state.current.dirty = dirty && !state.current.leaving;
  });

  useEffect(() => {
    const element = dialog.current!;
    const confirm = () =>
      new Promise<boolean>((resolve) => {
        state.current.pending?.();
        const trigger = document.activeElement as HTMLElement | null;
        const finish = (value: boolean) => {
          element.removeEventListener("click", click);
          element.removeEventListener("cancel", cancel);
          state.current.pending = null;
          if (element.open) element.close();
          if (trigger?.isConnected) trigger.focus();
          resolve(value);
        };
        const click = (event: MouseEvent) => {
          const target = event.target as Element;
          if (target.closest("[data-cfg1-leave]")) finish(true);
          else if (target.closest("[data-cfg1-stay]")) finish(false);
        };
        const cancel = (event: Event) => {
          event.preventDefault();
          finish(false);
        };
        state.current.pending = () => finish(false);
        element.addEventListener("click", click);
        element.addEventListener("cancel", cancel);
        element.showModal();
        element.querySelector<HTMLElement>("[data-cfg1-stay]")?.focus();
      });
    const leave = (go: () => void) => {
      state.current.leaving = true;
      state.current.dirty = false;
      go();
    };
    const click = (event: MouseEvent) => {
      if (!state.current.dirty || event.defaultPrevented || event.button !== 0) return;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element).closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || (anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return;
      const target = new URL(anchor.href, location.href);
      if (target.origin !== location.origin || target.protocol !== location.protocol) return;
      if (target.pathname === location.pathname && target.search === location.search) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void confirm().then((discard) => {
        if (discard) leave(() => navigate(target.pathname + target.search + target.hash));
      });
    };
    // Botón atrás: el aviso llega antes que al enrutador; se vuelve a la página y se pregunta.
    let restoring = false;
    const pop = (event: PopStateEvent) => {
      if (restoring) {
        restoring = false;
        event.stopImmediatePropagation();
        return;
      }
      if (!state.current.dirty) return;
      event.stopImmediatePropagation();
      restoring = true;
      history.go(1);
      void confirm().then((discard) => {
        if (discard) leave(() => history.back());
      });
    };
    const unload = (event: BeforeUnloadEvent) => {
      if (!state.current.dirty) return;
      event.preventDefault();
    };
    document.addEventListener("click", click, true);
    window.addEventListener("popstate", pop, true);
    window.addEventListener("beforeunload", unload);
    const guard = state.current;
    return () => {
      guard.pending?.();
      document.removeEventListener("click", click, true);
      window.removeEventListener("popstate", pop, true);
      window.removeEventListener("beforeunload", unload);
    };
  }, [navigate]);

  return (
    <dialog ref={dialog} className="confirmation-dialog tm-discard" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{T("CFG1_R1_DISCARD_TITLE")}</h2>
      <div className="confirmation-actions">
        <button type="button" className="tm-btn secondary" data-cfg1-stay="">
          {T("CFG1_STAY")}
        </button>
        <button type="button" className="tm-btn primary" data-cfg1-leave="">
          {T("CFG1_R1_DISCARD")}
        </button>
      </div>
    </dialog>
  );
}

/** Los enlaces a pantallas ya migradas de Equipo navegan sin recargar (como la navegación del portal actual). */
export function useInternalLinks(root: React.RefObject<HTMLElement | null>, isInternal: (path: string) => boolean) {
  const navigate = useNavigate();
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const click = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (event.target as Element).closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor || (anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return;
      const target = new URL(anchor.href, location.href);
      if (target.origin !== location.origin || !isInternal(target.pathname)) return;
      event.preventDefault();
      navigate(target.pathname + target.search + target.hash);
    };
    element.addEventListener("click", click);
    return () => element.removeEventListener("click", click);
  }, [root, navigate, isInternal]);
}
